import type { CustomerFileCategory } from '@/api/portalFiles';

/** One onboarding upload section. Mirrors the backend's per-category
 *  MIME allow-list + size cap in `server_py.routes.portal_files` — keep
 *  the two in sync (the server is the source of truth; this is the
 *  client-side pre-check + accept hint). */
export interface UploadCategoryMeta {
  category: CustomerFileCategory;
  label: string;
  description: string;
  /** Allowed filename extensions; null = any file type accepted. */
  extensions: string[] | null;
  maxBytes: number;
}

const MB = 1024 * 1024;

/** Ordered so the onboarding view renders GL → CoA → Invoices → Returns → Other. */
export const UPLOAD_CATEGORIES: UploadCategoryMeta[] = [
  {
    category: 'general_ledger',
    label: 'General ledger',
    description: 'Your transaction export from the accounting system (XLSX or CSV).',
    extensions: ['xlsx', 'xls', 'csv', 'pdf'],
    maxBytes: 50 * MB,
  },
  {
    category: 'chart_of_accounts',
    label: 'Chart of accounts',
    description: 'Your account list from the accounting system (XLSX, CSV, or PDF).',
    extensions: ['xlsx', 'xls', 'csv', 'pdf'],
    maxBytes: 10 * MB,
  },
  {
    category: 'invoice',
    label: 'Invoices',
    description: 'Supplier invoices backing up the transactions (PDF, image, or spreadsheet).',
    extensions: ['pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'csv'],
    maxBytes: 10 * MB,
  },
  {
    category: 'returns',
    label: 'Prior returns',
    description: 'GST/HST returns already filed for the period, plus any working copies.',
    extensions: ['pdf', 'xlsx', 'xls', 'csv'],
    maxBytes: 10 * MB,
  },
  {
    category: 'other',
    label: 'Other documents',
    description: 'Anything else — working papers, statements. Any file type.',
    extensions: null,
    maxBytes: 10 * MB,
  },
];

/** Bucket files by category, seeded from the vocabulary so every
 *  section renders (empty or not) and new categories need no UI edits.
 *  Legacy customer_invoice rows are backfilled to 'invoice'; any
 *  unexpected null falls into invoices as the safe default. */
export function groupByCategory<T extends { category?: CustomerFileCategory | null }>(
  items: T[],
): Record<CustomerFileCategory, T[]> {
  const groups = Object.fromEntries(
    UPLOAD_CATEGORIES.map((m) => [m.category, [] as T[]]),
  ) as Record<CustomerFileCategory, T[]>;
  for (const item of items) {
    const category = (item.category ?? 'invoice') as CustomerFileCategory;
    (groups[category] ?? groups.invoice).push(item);
  }
  return groups;
}

export type PortalTab = 'onboarding' | 'summary' | 'billing';

export interface PortalTabState {
  /** Whether the Summary tab is offered (only once a run is published). */
  showSummary: boolean;
  /** Which tab the portal lands on. */
  defaultTab: PortalTab;
}

/** Tab visibility + landing tab, driven by publish state. Before a run
 *  is published the customer sees onboarding only; once published the
 *  report becomes the landing tab and onboarding stays accessible. */
export function portalTabs(hasPublishedEngagement: boolean): PortalTabState {
  return hasPublishedEngagement
    ? { showSummary: true, defaultTab: 'summary' }
    : { showSummary: false, defaultTab: 'onboarding' };
}
